/**
 * Shared utility functions for metadata editors (Course and Resource)
 * Extracted from CourseMetadataEditor.vue for reusability
 */

/**
 * Set a value on a nested object path, creating intermediate objects if needed
 * @param obj - The object to modify
 * @param path - Dot-separated path (e.g., 'additionalInfo.Industry')
 * @param value - The value to set
 */
export const setNestedValue = (obj: any, path: string, value: any) => {
  const keys = path.split('.');
  let current = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    // Create nested object if it doesn't exist
    if (!current[key] || typeof current[key] !== 'object') {
      current[key] = {};
    }
    current = current[key];
  }
  current[keys[keys.length - 1]] = value;
};

/**
 * Get a value from a nested object path
 * @param obj - The object to read from
 * @param path - Dot-separated path (e.g., 'additionalInfo.Industry')
 * @param fieldConfig - Optional field config object to provide default values
 * @returns The value at the path, or undefined/default if not found
 */
export const getNestedValue = (obj: any, path: string, fieldConfig?: any) => {
  const value = path.split('.').reduce((acc, part) => acc && acc[part], obj);

  // If the value is undefined and we have a default in fieldConfig, return the default
  if (value === undefined && fieldConfig && fieldConfig[path]) {
    return fieldConfig[path].defaultValue;
  }

  return value;
};

/**
 * Generate a UUID v4 compatible string
 * @returns A UUID string
 */
export const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Convert HTML to plain text
 * @param html - The HTML string to convert
 * @param fieldType - Type of field: 'paragraph', 'single-paragraph', or 'list'
 * @returns Plain text string
 */
export const convertHtmlToText = (
  html: string,
  fieldType: 'paragraph' | 'single-paragraph' | 'list' = 'paragraph'
): string => {
  if (!html || typeof html !== 'string') return '';

  if (fieldType === 'list') {
    // Handle list conversion - extract li elements and convert to markdown format
    const liMatches = html.match(/<li[^>]*>(.*?)<\/li>/g);
    if (liMatches) {
      return liMatches
        .map(li => {
          // Extract content from li tag and clean it
          const content = li
            .replace(/<li[^>]*>(.*?)<\/li>/, '$1')
            .replace(/<br\s*\/?>/g, ' ') // Replace br tags with spaces
            .replace(/<[^>]*>/g, '') // Remove any remaining HTML tags
            .replace(/&nbsp;/g, ' ') // Replace non-breaking spaces
            .replace(/&amp;/g, '&') // Decode ampersands
            .replace(/&lt;/g, '<') // Decode less than
            .replace(/&gt;/g, '>') // Decode greater than
            .replace(/&quot;/g, '"') // Decode quotes
            .replace(/&#39;/g, "'") // Decode apostrophes
            .replace(/\s+/g, ' ') // Normalize whitespace
            .trim();
          return `- ${content}`;
        })
        .join('\n');
    }

    // Fallback: if no li tags found, treat as paragraph
    let text = html
      .replace(/<p[^>]*>/g, '') // Remove opening p tags
      .replace(/<\/p>/g, '\n') // Replace closing p tags with newlines
      .replace(/<br\s*\/?>/g, '\n') // Replace br tags with newlines
      .replace(/<[^>]*>/g, '') // Remove any remaining HTML tags
      .replace(/&nbsp;/g, ' ') // Replace non-breaking spaces
      .replace(/&amp;/g, '&') // Decode ampersands
      .replace(/&lt;/g, '<') // Decode less than
      .replace(/&gt;/g, '>') // Decode greater than
      .replace(/&quot;/g, '"') // Decode quotes
      .replace(/&#39;/g, "'") // Decode apostrophes
      .replace(/\n\s*\n/g, '\n') // Collapse multiple newlines
      .trim();

    // Split by line breaks and add dashes
    const lines = text.split('\n').filter(line => line.trim());
    return lines.map(line => `- ${line.trim()}`).join('\n');
  }

  // Handle paragraph conversion
  let text = html
    .replace(/<p[^>]*>/g, '') // Remove opening p tags
    .replace(/<\/p>/g, fieldType === 'single-paragraph' ? ' ' : '\n\n') // Replace closing p tags - single space for single-paragraph, double newlines for multiple paragraphs
    .replace(/<br\s*\/?>/g, fieldType === 'single-paragraph' ? ' ' : '\n') // Replace br tags - spaces for single-paragraph, newlines for multiple paragraphs
    .replace(/<[^>]*>/g, '') // Remove any remaining HTML tags
    .replace(/&nbsp;/g, ' ') // Replace non-breaking spaces
    .replace(/&amp;/g, '&') // Decode ampersands
    .replace(/&lt;/g, '<') // Decode less than
    .replace(/&gt;/g, '>') // Decode greater than
    .replace(/&quot;/g, '"') // Decode quotes
    .replace(/&#39;/g, "'") // Decode apostrophes
    .trim();

  // For multiple paragraphs, preserve paragraph breaks
  if (fieldType === 'paragraph') {
    // Normalize whitespace within paragraphs (but preserve paragraph breaks)
    // First, replace multiple spaces/newlines within a paragraph with single space
    text = text.replace(/[ \t]+/g, ' '); // Normalize spaces and tabs within paragraphs
    // Then, normalize multiple newlines to double newlines (paragraph breaks)
    text = text.replace(/\n\s*\n\s*\n+/g, '\n\n'); // Collapse multiple newlines but preserve paragraph breaks
    // Clean up any single newlines that aren't part of paragraph breaks (convert to spaces)
    text = text.replace(/([^\n])\n([^\n])/g, '$1 $2'); // Single newlines within paragraphs become spaces
  } else {
    // For single-paragraph, normalize all whitespace to single spaces
    text = text.replace(/\s+/g, ' ');
  }

  return text;
};

/**
 * Convert plain text to HTML
 * @param text - The plain text string to convert
 * @param fieldType - Type of field: 'paragraph', 'single-paragraph', or 'list'
 * @returns HTML string
 */
export const convertTextToHtml = (
  text: string,
  fieldType: 'paragraph' | 'single-paragraph' | 'list' = 'paragraph'
): string => {
  if (!text || typeof text !== 'string') return '';

  if (fieldType === 'list') {
    // Convert markdown-style list back to HTML
    const lines = text.split('\n').filter(line => line.trim());
    const listItems = lines
      .map(line => line.replace(/^-\s*/, '').trim()) // Remove leading dash and space
      .filter(item => item) // Remove empty items
      .map(item => `<li>${item.replace(/\s+/g, ' ')}</li>`) // Normalize whitespace
      .join('');

    return listItems ? `<ul>${listItems}</ul>` : '';
  } else if (fieldType === 'single-paragraph') {
    // Convert text back to HTML as single paragraph
    if (!text.trim()) return '';

    const normalizedText = text.replace(/\s+/g, ' ').trim();
    return normalizedText ? `<p>${normalizedText}</p>` : '';
  } else {
    // Convert text back to HTML with multiple paragraphs support
    if (!text.trim()) return '';

    // Split by double newlines to identify paragraphs
    const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim());

    if (paragraphs.length === 1) {
      // Single paragraph - normalize whitespace
      const normalizedText = paragraphs[0].replace(/\s+/g, ' ').trim();
      return normalizedText ? `<p>${normalizedText}</p>` : '';
    } else {
      // Multiple paragraphs - wrap each in <p> tags
      return paragraphs
        .map(p => p.replace(/\s+/g, ' ').trim()) // Normalize whitespace within each paragraph
        .filter(p => p) // Remove empty paragraphs
        .map(p => `<p>${p}</p>`)
        .join('');
    }
  }
};

/**
 * Deep comparison of two objects, handling special cases for metadata fields
 * Returns true if objects are different, false if they are the same
 * @param obj1 - First object to compare
 * @param obj2 - Second object to compare
 * @returns true if different, false if same
 */
export const compareObjects = (obj1: any, obj2: any): boolean => {
  // Handle null/undefined cases
  if (obj1 === obj2) return true;
  if (obj1 == null || obj2 == null) return false;

  // Get all keys from both objects
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  // Check if they have the same number of keys
  if (keys1.length !== keys2.length) {
    console.log('CompareObjects: Different number of keys!', { keys1, keys2 });
    return true;
  }

  // Check each property value
  for (const key of keys1) {
    if (!keys2.includes(key)) {
      console.log(`CompareObjects: Key "${key}" missing in obj2!`);
      return true; // Key missing in obj2
    }

    let val1 = obj1[key];
    let val2 = obj2[key];

    // Normalize null/undefined/empty string to be equivalent for comparison
    // This handles cases where form has defaultValue (null) but published data doesn't have the field (undefined)
    // We apply this normalization early, but special cases may override it
    const normalizeEmpty = (val: any) => {
      if (val === null || val === undefined || val === '') return null;
      return val;
    };

    // Subtopic lists: parallel string arrays; order-independent for dirty detection
    if (key === 'subtopicId' || key === 'subtopicTitle') {
      const normArr = (val: any) => {
        if (val === null || val === undefined) return null;
        if (!Array.isArray(val)) return `__nonarray__:${String(val)}`;
        if (val.length === 0) return null;
        return [...val].map(String).sort().join('\0');
      };
      if (normArr(val1) !== normArr(val2)) {
        return true;
      }
      continue;
    }

    // Special handling for replacedBy (0 and null should be treated as equivalent)
    // Legacy data may have 0 instead of null for "no replacement"
    if (key === 'replacedBy') {
      const normalizeReplacedBy = (val: any) => {
        if (val === null || val === undefined || val === '') return null;
        if (Number(val) === 0) return null; // Treat 0 as null
        return Number(val);
      };

      const normalizedVal1 = normalizeReplacedBy(val1);
      const normalizedVal2 = normalizeReplacedBy(val2);

      if (normalizedVal1 !== normalizedVal2) {
        console.log(`ReplacedBy comparison failed:`, {
          key,
          val1: `${val1} (${typeof val1})`,
          val2: `${val2} (${typeof val2})`,
          normalizedVal1,
          normalizedVal2
        });
        return true;
      }
      continue;
    }

    // Special handling for string boolean values (like flagship)
    if (key === 'flagship') {
      const normalizedVal1 =
        val1 === true ? 'true' : val1 === false ? 'false' : val1;
      const normalizedVal2 =
        val2 === true ? 'true' : val2 === false ? 'false' : val2;

      if (normalizedVal1 !== normalizedVal2) {
        console.log(`Flagship comparison failed:`, {
          key,
          val1: `${val1} (${typeof val1})`,
          val2: `${val2} (${typeof val2})`,
          normalizedVal1,
          normalizedVal2
        });
        return true;
      }
      continue;
    }

    // Special handling for qualityIndex (string vs number comparison)
    if (key === 'qualityIndex') {
      const normalizedVal1 =
        val1 === null || val1 === undefined || val1 === ''
          ? null
          : String(val1);
      const normalizedVal2 =
        val2 === null || val2 === undefined || val2 === ''
          ? null
          : String(val2);

      if (normalizedVal1 !== normalizedVal2) {
        console.log(`QualityIndex comparison failed:`, {
          key,
          val1: `${val1} (${typeof val1})`,
          val2: `${val2} (${typeof val2})`,
          normalizedVal1,
          normalizedVal2
        });
        return true;
      }
      continue;
    }

    // Special handling for array fields (accredited, TargetLearners)
    // These are now arrays (after migration), but may still encounter legacy strings
    // Sort arrays before comparing to ensure consistent comparison regardless of order
    if (key === 'accredited' || key === 'TargetLearners') {
      const normalizeArrayField = (val: any) => {
        if (val === null || val === undefined || val === '') return '';
        if (Array.isArray(val) && val.length === 0) return '';
        if (Array.isArray(val)) {
          // Sort array and join for consistent comparison
          return [...val].sort().join(', ');
        }
        // Legacy string format - convert to array, sort, and join
        if (typeof val === 'string' && val.trim()) {
          return val
            .split(/,\s*|;\s*|\s+and\s+/i)
            .map(item => item.trim())
            .filter(item => item.length > 0)
            .sort()
            .join(', ');
        }
        return String(val);
      };

      const normalizedVal1 = normalizeArrayField(val1);
      const normalizedVal2 = normalizeArrayField(val2);

      if (normalizedVal1 !== normalizedVal2) {
        console.log(`Array field comparison failed:`, {
          key,
          val1: `${val1} (${typeof val1})`,
          val2: `${val2} (${typeof val2})`,
          normalizedVal1,
          normalizedVal2
        });
        return true;
      }
      continue;
    }

    // Special handling for HK hours (numeric comparison with null handling)
    if (key === 'hkcpdHours' || key === 'hkrmeHours') {
      const normalizeHKHours = (val: any) => {
        if (val === null || val === undefined) return null;
        return Number(val);
      };

      const normalizedVal1 = normalizeHKHours(val1);
      const normalizedVal2 = normalizeHKHours(val2);

      if (normalizedVal1 !== normalizedVal2) {
        return true;
      }
      continue;
    }

    // Special handling for relatedTo and redundancy arrays - sort before comparing
    // These arrays may be in different orders but should be considered equal if they contain the same values
    if (key === 'relatedTo' || key === 'redundancy') {
      if (!Array.isArray(val1) || !Array.isArray(val2)) {
        // One is not an array - compare as-is
        if (val1 !== val2) {
          return true;
        }
        continue;
      }

      // Sort arrays numerically for consistent comparison
      const sorted1 = [...val1].map(v => Number(v)).sort((a, b) => a - b);
      const sorted2 = [...val2].map(v => Number(v)).sort((a, b) => a - b);

      if (sorted1.length !== sorted2.length) {
        return true;
      }
      for (let i = 0; i < sorted1.length; i++) {
        if (sorted1[i] !== sorted2[i]) {
          return true;
        }
      }
      continue;
    }

    // Handle arrays (general case - order matters)
    if (Array.isArray(val1) && Array.isArray(val2)) {
      if (val1.length !== val2.length) {
        return true;
      }
      for (let i = 0; i < val1.length; i++) {
        if (val1[i] !== val2[i]) {
          return true;
        }
      }
      continue;
    }

    // Normalize null/undefined/empty for final comparison (if not already handled by special cases)
    // This handles cases like legislation where form has null (defaultValue) but published has undefined
    const normalizedVal1 = normalizeEmpty(val1);
    const normalizedVal2 = normalizeEmpty(val2);

    // If both normalized to null, they're equal
    if (normalizedVal1 === null && normalizedVal2 === null) {
      continue;
    }

    // Handle primitive values (using normalized values)
    if (normalizedVal1 !== normalizedVal2) {
      console.log(`CompareObjects: Value difference found for key "${key}"`, {
        key,
        val1: `${val1} (${typeof val1})`,
        val2: `${val2} (${typeof val2})`
      });
      return true;
    }
  }

  return false; // No differences found
};

/**
 * Strip HTML tags from a string
 * @param html - The HTML string (or any value)
 * @returns Plain text string with HTML tags removed
 */
export const stripHtml = (html: any) => {
  if (typeof html !== 'string') return html;
  return html.replace(/<[^>]*>/g, '').trim();
};

/**
 * Compare two values for equality, handling special cases
 * Used for comparing field values in status calculations
 * @param a - First value
 * @param b - Second value
 * @returns true if values are equal, false otherwise
 */
export const areValuesEqual = (a: any, b: any): boolean => {
  // Handle null/undefined/empty string/empty array equivalence
  const normalizeEmptyValue = (val: any) => {
    if (val === null || val === undefined || val === '') return null;
    // Treat empty arrays the same as undefined/null for comparison purposes
    if (Array.isArray(val) && val.length === 0) return null;
    return val;
  };

  const normalizedA = normalizeEmptyValue(a);
  const normalizedB = normalizeEmptyValue(b);

  // Strict equality check (handles primitives, null, undefined)
  if (normalizedA === normalizedB) return true;

  // Special handling for boolean vs undefined/null (for fields that might not exist in published data)
  // Treat undefined/null as equivalent to false for boolean fields
  if (
    typeof normalizedA === 'boolean' &&
    (normalizedB === null || normalizedB === undefined)
  ) {
    return normalizedA === false;
  }
  if (
    typeof normalizedB === 'boolean' &&
    (normalizedA === null || normalizedA === undefined)
  ) {
    return normalizedB === false;
  }

  // Special handling for boolean vs string "true"/"false" (for flagship and other boolean fields)
  // Convert boolean true/false to string "true"/"false" for comparison
  if (typeof normalizedA === 'boolean' || typeof normalizedB === 'boolean') {
    const strA =
      normalizedA === true
        ? 'true'
        : normalizedA === false
          ? 'false'
          : String(normalizedA);
    const strB =
      normalizedB === true
        ? 'true'
        : normalizedB === false
          ? 'false'
          : String(normalizedB);
    if (strA === strB) return true;
  }

  // Handle string comparison with HTML stripping (for legislation and other HTML fields)
  if (typeof normalizedA === 'string' && typeof normalizedB === 'string') {
    const strippedA = stripHtml(normalizedA).trim();
    const strippedB = stripHtml(normalizedB).trim();
    // Treat empty strings as equal to null
    if (
      (strippedA === '' && normalizedB === null) ||
      (strippedB === '' && normalizedA === null)
    ) {
      return true;
    }
    return strippedA === strippedB;
  }

  // Handle arrays (both must be non-empty arrays at this point)
  if (Array.isArray(normalizedA) && Array.isArray(normalizedB)) {
    if (normalizedA.length !== normalizedB.length) return false;
    // Deep comparison for array elements
    return normalizedA.every((val, idx) =>
      areValuesEqual(val, normalizedB[idx])
    );
  }

  // Handle objects (shallow comparison)
  if (
    normalizedA &&
    normalizedB &&
    typeof normalizedA === 'object' &&
    typeof normalizedB === 'object' &&
    !Array.isArray(normalizedA) &&
    !Array.isArray(normalizedB)
  ) {
    const keysA = Object.keys(normalizedA);
    const keysB = Object.keys(normalizedB);
    if (keysA.length !== keysB.length) return false;
    return keysA.every(key =>
      areValuesEqual(normalizedA[key], normalizedB[key])
    );
  }

  // Handle string comparison (strip HTML for comparison)
  if (typeof normalizedA === 'string' && typeof normalizedB === 'string') {
    const strippedA = stripHtml(normalizedA).trim();
    const strippedB = stripHtml(normalizedB).trim();
    return strippedA === strippedB;
  }

  // Handle number comparison (convert strings to numbers if possible)
  if (typeof normalizedA === 'number' && typeof normalizedB === 'number') {
    return normalizedA === normalizedB;
  }

  // Try converting both to strings for comparison
  if (normalizedA != null && normalizedB != null) {
    return String(normalizedA) === String(normalizedB);
  }

  return false;
};

/**
 * Extract section fields from form data
 * Returns object with FIELD_CONFIG paths as keys (preserves nested paths)
 * Used by ResourceMetadataEditor to extract section data for saving
 * @param sectionName - Name of the section (e.g., 'sectionTitle')
 * @param formData - The form data object
 * @param fieldConfig - The FIELD_CONFIG object from resourceMetadataConfig
 * @returns Object with field paths as keys and form values as values
 */
export const extractResourceSectionFields = (
  sectionName: string,
  formData: any,
  fieldConfig: Record<string, { section: string; [key: string]: any }>
): Record<string, any> => {
  // Get all fields for this section from FIELD_CONFIG
  const sectionFields = Object.keys(fieldConfig).filter(
    path => fieldConfig[path].section === sectionName
  );

  // Extract values using full paths (not just field names)
  // This preserves nested paths like "additionalInfo.Industry"
  const sectionData: Record<string, any> = {};
  sectionFields.forEach(fieldPath => {
    const value = getNestedValue(formData, fieldPath);
    // Use full path as key to preserve nesting structure
    sectionData[fieldPath] = value;
  });

  return sectionData;
};

/**
 * Calculate section status by comparing library vs saved_items JSONB
 * Uses FIELD_CONFIG to group fields by section
 * Used by ResourceMetadataEditor to determine section status badges
 * @param sectionName - Name of the section (e.g., 'sectionTitle')
 * @param libraryData - Published data from library
 * @param savedJsonb - Draft data from saved_items_table JSONB
 * @param fieldConfig - The FIELD_CONFIG object from resourceMetadataConfig
 * @param productType - The product type (e.g., 'video', 'webinar', 'blog', 'guide', 'template')
 * @returns Status string (will be used with SECTION_STATUSES enum in component)
 */
export const calculateResourceSectionStatus = (
  sectionName: string,
  libraryData: any,
  savedJsonb: any,
  fieldConfig: Record<
    string,
    {
      section: string;
      required: boolean;
      showFor?: string[] | null;
      requiredFor?: string[] | null;
      [key: string]: any;
    }
  >,
  productType?: string
): string => {
  // 1. Get all fields for this section from FIELD_CONFIG, filtered by product type
  const sectionFields = Object.keys(fieldConfig).filter(path => {
    const config = fieldConfig[path];
    // Must be in this section
    if (config.section !== sectionName) {
      return false;
    }

    // If productType is provided, filter by showFor and requiredFor
    if (productType) {
      // Check if field should be shown for this product type
      if (config.showFor !== null && config.showFor !== undefined) {
        if (!config.showFor.includes(productType)) {
          return false; // Field not shown for this product type
        }
      }

      // Check if field is required for this product type
      if (config.requiredFor !== null && config.requiredFor !== undefined) {
        if (!config.requiredFor.includes(productType)) {
          // Field is not required for this product type, but we still include it for comparison
          // (it might exist in the data even if not required)
        }
      }
    }

    return true;
  });

  if (sectionFields.length === 0) {
    return 'NOT_STARTED'; // No fields in this section
  }

  // 2. Check if section has ANY published data (for determining if this is a new resource vs existing)
  const hasAnyPublishedData =
    libraryData && Object.keys(libraryData).length > 0;

  // 3. Check if section has ALL required fields in published data
  const hasAllRequiredPublishedData =
    libraryData &&
    sectionFields.every(path => {
      const config = fieldConfig[path];
      // Only check required fields for published data
      if (!config.required) {
        return true; // Optional fields don't need to exist in published data
      }

      // Check if field is required for this product type
      if (
        productType &&
        config.requiredFor !== null &&
        config.requiredFor !== undefined
      ) {
        if (!config.requiredFor.includes(productType)) {
          return true; // Field not required for this product type
        }
      }

      // Check if the path actually exists in the published data (not just default values)
      const value = getNestedValue(libraryData, path);
      // Consider a field as having data if it's not null, undefined, or an empty string/array
      return (
        value !== null &&
        value !== undefined &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0)
      );
    });

  // 4. Compare each field between libraryData and savedJsonb
  const hasChanges = sectionFields.some(fieldPath => {
    const libraryValue = getNestedValue(libraryData, fieldPath);
    const savedValue = getNestedValue(savedJsonb, fieldPath);
    const areEqual = areValuesEqual(libraryValue, savedValue);

    // Special debug logging for sectionInfo
    if (sectionName === 'sectionInfo' && !areEqual) {
      console.log(
        `[calculateResourceSectionStatus] sectionInfo field "${fieldPath}" differs:`,
        {
          libraryValue,
          savedValue,
          libraryType: typeof libraryValue,
          savedType: typeof savedValue,
          areEqual: areValuesEqual(libraryValue, savedValue)
        }
      );
    }

    return !areEqual;
  });

  // Special debug logging for sectionInfo
  if (sectionName === 'sectionInfo') {
    console.log(
      `[calculateResourceSectionStatus] sectionInfo status calculation:`,
      {
        sectionFields,
        hasAnyPublishedData,
        hasAllRequiredPublishedData,
        hasChanges,
        libraryData,
        savedJsonb
      }
    );
  }

  // 5. Check if all required fields are present in savedJsonb
  const hasAllRequiredFields = sectionFields.every(path => {
    const config = fieldConfig[path];
    if (!config.required) {
      return true; // Optional fields don't need to exist
    }

    // Check if field is required for this product type
    if (
      productType &&
      config.requiredFor !== null &&
      config.requiredFor !== undefined
    ) {
      if (!config.requiredFor.includes(productType)) {
        return true; // Field not required for this product type
      }
    }

    const value = getNestedValue(savedJsonb, path);
    return (
      value !== null &&
      value !== undefined &&
      value !== '' &&
      !(Array.isArray(value) && value.length === 0)
    );
  });

  // 6. Determine status based on conditions
  if (!hasAnyPublishedData) {
    // New resource (not yet published)
    if (hasAllRequiredFields) {
      return 'READY_TO_PUBLISH';
    } else {
      return 'NOT_STARTED';
    }
  } else {
    // Existing resource (already published)
    if (hasChanges) {
      if (hasAllRequiredFields) {
        return 'READY_TO_UPDATE';
      } else {
        return 'UNSAVED_CHANGES';
      }
    } else {
      if (hasAllRequiredPublishedData) {
        return 'PUBLISHED';
      } else {
        return 'NOT_STARTED';
      }
    }
  }
};
